import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ProductCard } from '../components/ProductCard';
import { SearchModal } from '../components/SearchModal';
import { CategoryFilter } from '../components/CategoryFilter';
import { FlashSaleTimer } from '../components/FlashSaleTimer';
import { MobileNav } from '../components/MobileNav';
import { Logo } from '../components/Logo';
import { WinnerTicker } from '../components/WinnerTicker';
import { NotificationBell } from '../components/NotificationBell';
import { AccountHUD, AccountHUDMobile } from '../components/AccountHUD';
import { TrendingUp, ArrowRight, Sparkles, ChevronLeft, ChevronRight, Trophy, Star, Zap, Crown, Flame, ShieldCheck, Truck, CreditCard, Radio, Award, Eye, Search } from 'lucide-react';
import { API } from '../config';

const playfairFont = "'Playfair Display', serif";
const outfitFont = "'Outfit', sans-serif";
const fmt = (n) => new Intl.NumberFormat('id-ID').format(Number(n) || 0);

const FALLBACK_HERO =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 700"><defs><linearGradient id="g" x1="0%25" y1="0%25" x2="100%25" y2="100%25"><stop offset="0%25" stop-color="%23032014"/><stop offset="100%25" stop-color="%23080C0A"/></linearGradient></defs><rect fill="url(%23g)" width="1600" height="700"/></svg>';
const FALLBACK_KUJI =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><rect fill="%23032014" width="800" height="400"/><text x="400" y="200" fill="%23D4AF37" font-family="serif" font-size="32" text-anchor="middle" dy=".3em" font-style="italic">Kuji</text></svg>';

export const Home = () => {
  const navigate = useNavigate();
  const [flashSaleProducts, setFlashSaleProducts] = useState([]);
  const [regularProducts, setRegularProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [promos, setPromos] = useState([]);
  const [kujiEvents, setKujiEvents] = useState([]);
  const [hotEvents, setHotEvents] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const flashParams = new URLSearchParams({ flash_sale: 'true' });
      const { data: flashData } = await axios.get(`${API}/products?${flashParams}`);
      setFlashSaleProducts(flashData);

      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      const { data: regularData } = await axios.get(`${API}/products?${params}`);
      setRegularProducts(regularData.filter((p) => !p.is_flash_sale));
    } catch (e) {
      console.error('Failed to fetch products:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchTerm]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    axios.get(`${API}/hero-banners`).then((r) => setBanners(r.data || [])).catch(() => setBanners([]));
    axios.get(`${API}/promo-banners`).then((r) => setPromos(r.data || [])).catch(() => setPromos([]));
    axios.get(`${API}/kuji/events`).then((r) => setKujiEvents(r.data || [])).catch(() => setKujiEvents([]));
    axios.get(`${API}/kuji/events-hot`, { params: { limit: 8 } }).then((r) => setHotEvents(r.data || [])).catch(() => setHotEvents([]));
  }, []);

  return (
    <div className="min-h-screen pb-24" style={{ background: '#080C0A', fontFamily: outfitFont }}>
      <header className="fixed top-0 w-full z-50 backdrop-blur-2xl transition-all duration-300" style={{ background: 'rgba(8, 12, 10, 0.5)', borderBottom: '1px solid rgba(212,175,55,0.15)' }} data-testid="top-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center" data-testid="navbar-logo">
            <Logo size={42} withText animated />
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm" style={{ color: '#A3B8AC' }}>
            <a href="#products" className="hover:text-[#D4AF37] transition-colors">Shop</a>
            <a href="#kuji" className="hover:text-[#D4AF37] transition-colors">Kuji</a>
            <a href="#promo" className="hover:text-[#D4AF37] transition-colors">Promo</a>
            <button onClick={() => navigate('/live-winner')} className="hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="top-live-winner-link">
              <Radio className="w-3.5 h-3.5" style={{ color: '#EF4444' }} /> Live Winner
            </button>
            <button onClick={() => setSearchOpen(true)} className="hover:text-[#D4AF37] transition-colors flex items-center gap-1" data-testid="open-search-btn" aria-label="Cari produk">
              <Search className="w-4 h-4" />
            </button>
            <AccountHUD />
          </nav>
          <div className="md:hidden flex items-center gap-3">
            <button onClick={() => setSearchOpen(true)} aria-label="Cari produk" data-testid="open-search-btn-mobile">
              <Search className="w-5 h-5" style={{ color: '#A3B8AC' }} />
            </button>
            <NotificationBell />
          </div>
        </div>
        <AccountHUDMobile />
      </header>

      {/* Shop-first Hero — LimHappyShop adalah toko online; Kuji hanyalah fitur tambahan */}
      <ShopHero navigate={navigate} />

      {/* Admin-managed promotional banners (kept as a secondary highlight, smaller) */}
      <AdminFeaturedBanners banners={banners} onCta={(link) => link && navigate(link)} />

      {/* Subtle live winner ticker */}
      <WinnerTicker />

      {/* Search & Category Filter */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 space-y-6">
        <CategoryFilter selectedCategory={selectedCategory} onSelectCategory={setSelectedCategory} />
      </div>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Featured Kuji Event(s) */}
      <FeaturedKujiSection events={kujiEvents} navigate={navigate} />

      {/* Hot / Most Played Events */}
      <HotKujiSection events={hotEvents} navigate={navigate} />

      {/* Kuji by Category */}
      <KujiByCategorySection events={kujiEvents} navigate={navigate} />

      {/* Promo banners (admin-managed) */}
      <PromoBannersStrip promos={promos} navigate={navigate} />

      {/* Promo Section (Flash Sale) */}
      {flashSaleProducts.length > 0 && (
        <section id="promo" className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
          <div
            className="relative rounded-3xl overflow-hidden p-8 md:p-12 backdrop-blur-sm"
            style={{
              backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/42e76d41-952c-43fd-ac53-35da8eec859d/images/6a608952428167e18c408ff16345648943671fbf3c4f14a7ac2506f62b748dab.png')",
              backgroundSize: 'cover', backgroundPosition: 'center'
            }}
            data-testid="flash-sale-section"
          >
            <div className="absolute inset-0" style={{ background: 'rgba(3, 32, 20, 0.82)' }} />
            <div className="relative z-10">
              <div className="flex flex-wrap items-center justify-between gap-6 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4" style={{ color: '#D4AF37' }} />
                    <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Promo · Limited Time</span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Flash Sale</h2>
                </div>
                {flashSaleProducts[0]?.flash_sale_end_time && (
                  <FlashSaleTimer endTime={flashSaleProducts[0].flash_sale_end_time} />
                )}
              </div>
              <p className="mb-8 text-sm" style={{ color: '#A3B8AC' }}>Curated pieces at exceptional value, available briefly.</p>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {flashSaleProducts.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* New Kuji Events */}
      <NewKujiSection events={kujiEvents} navigate={navigate} />

      {/* The Collection (regular products) */}
      <section id="products" className="max-w-7xl mx-auto px-6 lg:px-12 mb-20">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="h-px w-8" style={{ background: '#D4AF37' }} />
              <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>The Collection</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Curated for You</h2>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4AF37', borderTopColor: 'transparent' }} />
          </div>
        ) : regularProducts.length === 0 ? (
          <div className="text-center py-20"><p style={{ color: '#A3B8AC' }}>No products found</p></div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="products-grid">
            {regularProducts.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>

      {/* Trust strip */}
      <TrustSection />

      {/* CTA register / top up */}
      <CtaSection navigate={navigate} />

      <MobileNav />
    </div>
  );
};

// ----- Shop-first Hero (LimHappyShop is an online store; Kuji is supporting feature) -----
const ShopHero = ({ navigate }) => {
  const tiles = [
    { label: 'Smartphone', img: 'https://images.unsplash.com/photo-1592899677977-9c10ca588bbd?auto=format&fit=crop&w=500&q=70', span: 'col-span-2 row-span-2' },
    { label: 'Sneakers', img: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=500&q=70', span: '' },
    { label: 'Headset', img: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?auto=format&fit=crop&w=400&q=70', span: '' },
    { label: 'TCG Cards', img: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?auto=format&fit=crop&w=400&q=70', span: '' },
    { label: 'Anime Figure', img: 'https://images.unsplash.com/photo-1601814933824-fd0b574dd592?auto=format&fit=crop&w=400&q=70', span: '' },
    { label: 'Smartwatch', img: 'https://images.unsplash.com/photo-1546868871-7041f2a55e12?auto=format&fit=crop&w=400&q=70', span: '' },
    { label: 'Console', img: 'https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?auto=format&fit=crop&w=400&q=70', span: '' },
    { label: 'Tablet', img: 'https://images.unsplash.com/photo-1561154464-82e9adf32764?auto=format&fit=crop&w=400&q=70', span: '' },
  ];
  return (
    <section
      className="relative pt-28 md:pt-32 pb-12 md:pb-20 overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top right, rgba(212,175,55,0.10), transparent 55%), linear-gradient(180deg, #061611 0%, #080C0A 70%)',
      }}
      data-testid="shop-hero"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* LEFT: Copy */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="lg:col-span-6 order-1"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5" style={{ background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="text-[10px] tracking-[0.25em] uppercase" style={{ color: '#D4AF37' }}>Toko Online Terpercaya</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] mb-4" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
            Barang <span style={{ color: '#D4AF37' }}>Terjamin</span>,
            <br />
            Termurah, Terlengkap, <span style={{ color: '#D4AF37' }}>Terbaik</span>.
          </h1>
          <p className="text-base md:text-lg leading-relaxed mb-6 max-w-xl" style={{ color: '#CFE0D5' }}>
            Belanja produk original, gadget, elektronik, fashion, koleksi, hingga ribuan produk pilihan lainnya dalam satu marketplace terpercaya — pengiriman ke seluruh Indonesia.
          </p>

          <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mb-8 max-w-xl">
            {[
              'Produk 100% Original',
              'Harga Kompetitif & Transparan',
              'Pengiriman Seluruh Indonesia',
              'Pembayaran Aman & Mudah',
            ].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm" style={{ color: '#E6EFE9' }}>
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.45)' }}
                >
                  <span style={{ color: '#D4AF37', fontSize: 11, lineHeight: 1 }}>✓</span>
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap gap-3">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                const el = document.getElementById('products');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-semibold tracking-wide"
              style={{ background: '#D4AF37', color: '#080C0A', boxShadow: '0 12px 32px rgba(212,175,55,0.28)' }}
              data-testid="shop-hero-shop-btn"
            >
              BELANJA SEKARANG
              <ArrowRight className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                const el = document.getElementById('promo');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-medium tracking-wide"
              style={{ background: 'rgba(255,255,255,0.04)', color: '#F8FAF9', border: '1px solid rgba(212,175,55,0.35)' }}
              data-testid="shop-hero-promo-btn"
            >
              LIHAT PROMO
            </motion.button>
          </div>
        </motion.div>

        {/* RIGHT: Premium product collage */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut', delay: 0.15 }}
          className="lg:col-span-6 order-2 relative"
          data-testid="shop-hero-collage"
        >
          <div className="absolute -inset-6 -z-0 rounded-[40px] blur-3xl" style={{ background: 'radial-gradient(closest-side, rgba(212,175,55,0.18), transparent 70%)' }} />
          <div className="relative grid grid-cols-4 grid-rows-3 gap-2 sm:gap-3">
            {tiles.map((t, i) => (
              <motion.div
                key={t.label}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + i * 0.05, ease: 'easeOut' }}
                className={`relative rounded-2xl overflow-hidden ${t.span}`}
                style={{ background: '#0c1f17', border: '1px solid rgba(212,175,55,0.18)', aspectRatio: t.span.includes('row-span-2') ? '1 / 1' : '1 / 1' }}
              >
                <img
                  src={t.img}
                  alt={t.label}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,12,10,0) 55%, rgba(8,12,10,0.78) 100%)' }} />
                <span className="absolute bottom-1.5 left-2 text-[10px] font-medium tracking-wide" style={{ color: '#F8FAF9', textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}>
                  {t.label}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ----- Admin-managed featured banners (secondary strip; below shop hero) -----
const AdminFeaturedBanners = ({ banners, onCta }) => {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!banners || banners.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners]);

  if (!banners || banners.length === 0) return null;
  const current = banners[index];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 -mt-2 mb-8" data-testid="admin-featured-banners">
      <motion.div
        key={current.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        onClick={() => onCta(current.cta_link)}
        className="relative rounded-3xl overflow-hidden cursor-pointer h-44 md:h-56"
        style={{ border: '1px solid rgba(212,175,55,0.18)' }}
      >
        <img
          src={(isMobile && current.mobile_image_url) ? current.mobile_image_url : current.image_url}
          alt={current.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(8,12,10,0.85) 0%, rgba(8,12,10,0.4) 50%, rgba(8,12,10,0.2) 100%)' }} />
        <div className="absolute inset-0 flex items-center px-6 md:px-10">
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.25em] mb-2" style={{ color: '#D4AF37' }}>Featured</p>
            <h3 className="text-2xl md:text-3xl font-light mb-1" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>{current.title}</h3>
            {current.subtitle && <p className="text-sm" style={{ color: '#CFE0D5' }}>{current.subtitle}</p>}
            {current.cta_label && (
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold" style={{ color: '#D4AF37' }}>
                {current.cta_label} <ArrowRight className="w-3 h-3" />
              </span>
            )}
          </div>
        </div>
        {banners.length > 1 && (
          <div className="absolute bottom-3 right-4 flex gap-1.5">
            {banners.map((_, i) => (
              <span key={i} className="h-1 rounded-full" style={{ width: i === index ? 20 : 6, background: i === index ? '#D4AF37' : 'rgba(255,255,255,0.3)' }} />
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
};

// ----- Featured kuji events strip -----
const FeaturedKujiSection = ({ events, navigate }) => {
  const featured = events.filter((e) => e.featured && ['active', 'paused'].includes(e.status)).slice(0, 6);
  if (featured.length === 0) return null;
  return (
    <section id="kuji" className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="featured-kuji-section">
      <div className="flex items-center gap-2 mb-1">
        <Star className="w-4 h-4" style={{ color: '#D4AF37' }} />
        <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Featured</span>
      </div>
      <h2 className="text-3xl md:text-4xl font-light mb-6" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Featured Kuji Events</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {featured.map((ev) => (
          <KujiEventCard key={ev.id} event={ev} onClick={() => navigate(`/kuji/${ev.slug}`)} variant="featured" />
        ))}
      </div>
    </section>
  );
};

// ----- Kuji grouped by category -----
const KujiByCategorySection = ({ events, navigate }) => {
  const live = events.filter((e) => ['active', 'paused', 'sold_out'].includes(e.status) && (e.category || '').trim());
  if (live.length === 0) return null;
  const groups = {};
  for (const ev of live) {
    const k = (ev.category || '').trim();
    if (!groups[k]) groups[k] = [];
    groups[k].push(ev);
  }
  const categories = Object.keys(groups);
  if (categories.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16 space-y-12" data-testid="kuji-by-category-section">
      {categories.map((cat) => (
        <div key={cat}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="w-4 h-4" style={{ color: '#D4AF37' }} />
                <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>{cat}</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Kuji {cat}</h3>
            </div>
            <button onClick={() => navigate('/kuji')} className="text-xs flex items-center gap-1" style={{ color: '#D4AF37' }}>
              See all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {groups[cat].slice(0, 4).map((ev) => (
              <KujiEventCard key={ev.id} event={ev} onClick={() => navigate(`/kuji/${ev.slug}`)} variant="compact" />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
};

// ----- Newest Kuji events -----
const NewKujiSection = ({ events, navigate }) => {
  const sorted = [...events]
    .filter((e) => ['active', 'paused'].includes(e.status))
    .sort((a, b) => new Date(b.activated_at || b.created_at || 0) - new Date(a.activated_at || a.created_at || 0))
    .slice(0, 4);
  if (sorted.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="new-kuji-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4" style={{ color: '#D4AF37' }} />
            <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Just Launched</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>New Kuji Events</h2>
        </div>
        <button onClick={() => navigate('/kuji')} className="text-sm flex items-center gap-1.5" style={{ color: '#D4AF37' }} data-testid="see-all-kuji-btn">
          See all <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {sorted.map((ev) => (
          <KujiEventCard key={ev.id} event={ev} onClick={() => navigate(`/kuji/${ev.slug}`)} variant="compact" />
        ))}
      </div>
    </section>
  );
};

// ----- Kuji event card (shared) -----
const KujiEventCard = ({ event, onClick, variant = 'compact' }) => {
  const remaining = event.remaining_slots || 0;
  const total = event.total_slots || 1;
  const pct = Math.max(0, Math.min(100, (remaining / total) * 100));
  const tall = variant === 'featured';
  const almostOut = total > 0 && remaining > 0 && pct < 15;
  return (
    <motion.div
      whileHover={{ y: -5 }}
      onClick={onClick}
      className="cursor-pointer rounded-2xl overflow-hidden backdrop-blur-xl"
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
      data-testid={`home-kuji-card-${event.id}`}
    >
      <div
        className={tall ? 'aspect-[16/10]' : 'aspect-[16/11]'}
        style={{ backgroundImage: `url(${event.banner_url || event.cover_url || FALLBACK_KUJI})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="w-full h-full" style={{ background: 'linear-gradient(180deg, rgba(8,12,10,0) 50%, rgba(8,12,10,0.88) 100%)' }} />
      </div>
      <div className="p-3 md:p-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wider" style={{ color: '#D4AF37' }}>{event.category || 'Kuji'}</span>
          <div className="flex gap-1">
            {event.status === 'sold_out' && (<span className="text-[10px] font-bold" style={{ color: '#EF4444' }}>SOLD OUT</span>)}
            {almostOut && event.status !== 'sold_out' && (<span className="text-[10px] font-bold px-1.5 rounded-full" style={{ background: 'rgba(239,68,68,0.18)', color: '#EF4444' }}>HOT</span>)}
            {event.pinned && (<span className="text-[10px] font-bold" style={{ color: '#D4AF37' }}>📌</span>)}
          </div>
        </div>
        <p className="text-base font-medium truncate mb-1" style={{ color: '#F8FAF9', fontFamily: playfairFont }}>{event.name}</p>
        <div className="flex items-center justify-between text-xs mb-2">
          <span style={{ color: '#D4AF37' }}>{fmt(event.coin_price_per_draw)} coins</span>
          <span style={{ color: '#A3B8AC' }}>Sisa {fmt(remaining)}/{fmt(total)}</span>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #D4AF37, #F0C955)' }} />
        </div>
        {typeof event.total_draws === 'number' && event.total_draws > 0 && (
          <p className="text-[10px] mt-1.5" style={{ color: '#A3B8AC' }}>🔥 {fmt(event.total_draws)} draws</p>
        )}
      </div>
    </motion.div>
  );
};

// ----- Hot / most-played section -----
const HotKujiSection = ({ events, navigate }) => {
  if (!events || events.length === 0) return null;
  const top = events.slice(0, 8);
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="hot-kuji-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-4 h-4" style={{ color: '#EF4444' }} />
            <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Paling Banyak Dimainkan</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-light" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>Hot Events</h2>
        </div>
        <button onClick={() => navigate('/kuji')} className="text-sm flex items-center gap-1.5" style={{ color: '#D4AF37' }}>
          See all <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {top.map((ev) => (
          <KujiEventCard key={ev.id} event={ev} onClick={() => navigate(`/kuji/${ev.slug}`)} variant="compact" />
        ))}
      </div>
    </section>
  );
};

// ----- Promo banner strip (admin-managed) -----
const PromoBannersStrip = ({ promos, navigate }) => {
  if (!promos || promos.length === 0) return null;
  return (
    <section id="promo" className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="promo-strip">
      <div className="flex items-center gap-2 mb-5">
        <Award className="w-4 h-4" style={{ color: '#D4AF37' }} />
        <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Promo & Bonus</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {promos.map((p) => (
          <motion.div
            key={p.id}
            whileHover={{ y: -4 }}
            onClick={() => p.cta_link && navigate(p.cta_link)}
            className="cursor-pointer rounded-2xl overflow-hidden relative aspect-[16/9]"
            style={{ backgroundImage: `url(${p.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid rgba(212,175,55,0.2)' }}
            data-testid={`promo-card-${p.id}`}
          >
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(8,12,10,0.15) 30%, rgba(8,12,10,0.9) 100%)' }} />
            <div className="absolute bottom-3 left-4 right-4">
              <p className="text-lg font-medium" style={{ color: '#F8FAF9', fontFamily: playfairFont }}>{p.title}</p>
              {p.description && <p className="text-xs mt-1" style={{ color: '#CFE0D5' }}>{p.description}</p>}
              {p.cta_label && (
                <span className="inline-flex items-center gap-1 mt-2 text-xs font-semibold" style={{ color: '#D4AF37' }}>
                  {p.cta_label} <ArrowRight className="w-3 h-3" />
                </span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

// ----- Trust section -----
const TrustSection = () => {
  const items = [
    { icon: Award, label: 'Barang Terbaik', desc: 'Produk pilihan & kurasi premium' },
    { icon: ShieldCheck, label: '100% Original', desc: 'Item asli & terverifikasi' },
    { icon: Truck, label: 'Pengiriman Cepat', desc: 'JNE / ekspedisi resmi' },
    { icon: CreditCard, label: 'Pembayaran Aman', desc: 'QRIS / Mandiri / Coin' },
    { icon: Radio, label: 'Customer Support Responsif', desc: 'Bantuan kapan pun dibutuhkan' },
    { icon: Eye, label: 'Transparan & Terpercaya', desc: 'Harga & stok real-time' },
  ];
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="trust-section">
      <div className="flex items-center gap-2 mb-5">
        <ShieldCheck className="w-4 h-4" style={{ color: '#D4AF37' }} />
        <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Kenapa LimHappyShop</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {items.map(({ icon: Icon, label, desc }) => (
          <div key={label} className="rounded-2xl p-4 backdrop-blur-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,175,55,0.18)' }}>
            <Icon className="w-6 h-6 mb-2" style={{ color: '#D4AF37' }} />
            <p className="text-sm font-medium" style={{ color: '#F8FAF9' }}>{label}</p>
            <p className="text-[11px] mt-0.5" style={{ color: '#A3B8AC' }}>{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

// ----- CTA section -----
const CtaSection = ({ navigate }) => (
  <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-16" data-testid="cta-section">
    <div
      className="rounded-3xl p-8 md:p-12 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0c1f17 0%, #032014 60%, #080C0A 100%)', border: '1px solid rgba(212,175,55,0.25)' }}
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.18), transparent 70%)' }} />
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-4 h-4" style={{ color: '#D4AF37' }} />
          <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#D4AF37' }}>Mulai Belanja</span>
        </div>
        <h2 className="text-3xl md:text-4xl font-light mb-3" style={{ fontFamily: playfairFont, color: '#F8FAF9' }}>
          Belanja Pintar di LimHappyShop
        </h2>
        <p className="text-sm md:text-base mb-6 max-w-2xl" style={{ color: '#CFE0D5' }}>
          Dapatkan produk berkualitas dengan harga terbaik. Top up coin untuk transaksi lebih mudah & cepat di seluruh fitur kami.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => navigate('/topup')} className="px-7 py-3 rounded-full font-semibold flex items-center gap-2" style={{ background: '#D4AF37', color: '#080C0A' }} data-testid="cta-topup-btn">
            <CreditCard className="w-4 h-4" /> Top Up Coin
          </button>
          <button onClick={() => {
            const el = document.getElementById('products');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          }} className="px-7 py-3 rounded-full font-semibold flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.06)', color: '#F8FAF9', border: '1px solid rgba(212,175,55,0.3)' }} data-testid="cta-shop-btn">
            <ArrowRight className="w-4 h-4" /> Mulai Belanja
          </button>
        </div>
      </div>
    </div>
  </section>
);
